// Estimated set length for the Set Planner.
//
//   Σ known durations + missing × (mean of known ‖ 6:00) − MIX_OVERLAP × (n − 1)
//
// MIX_OVERLAP: house/techno blends run 16–32 bars, i.e. ~30–60s at 120–135
// BPM, so a flat 60s per transition. Tracks with no duration get the set's
// own average (a set is genre-coherent, so its tracks predict each other
// better than a global constant); with nothing known at all, 6:00 — a typical
// club original / extended mix. It's always an estimate (we can't know how
// long the DJ actually mixes, or whether they play an extended version), so
// the UI shows it with "≈" and never a "based on x of n" note.
import { parseDurationSec } from './lazyResolveCache';

export const MIX_OVERLAP_SEC = 60;
export const DEFAULT_TRACK_SEC = 6 * 60;

// `tracks` — anything shaped like { duration | durationSec } (planner nodes'
// `.track`, catalog rows, local DB rows). Returns whole seconds, or null when
// there are no tracks.
export function estimateSetSeconds(tracks) {
    const list = Array.isArray(tracks) ? tracks : [];
    const n = list.length;
    if (n === 0) return null;
    const known = [];
    for (const t of list) {
        const sec = parseDurationSec(t?.durationSec ?? t?.duration);
        if (sec && sec > 0) known.push(sec);
    }
    const knownSum = known.reduce((a, b) => a + b, 0);
    const fill = known.length ? knownSum / known.length : DEFAULT_TRACK_SEC;
    const total = knownSum + (n - known.length) * fill - MIX_OVERLAP_SEC * (n - 1);
    return Math.max(0, Math.round(total));
}

// "~52m" / "~1h 12m" / "~2h" — the tilde says "estimate" in one character
// (tried: "≈" alone, clock + "~", clock + "est." — the clock + tilde with a
// tight gap won as the least busy). Shown next to a clock glyph (SetLength in
// WhatsNextTab); no rounding beyond whole minutes. Null in → ''.
export function formatSetLength(totalSec) {
    if (totalSec == null || !Number.isFinite(totalSec)) return '';
    const mins = Math.round(totalSec / 60);
    if (mins < 60) return `~${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `~${h}h ${m}m` : `~${h}h`;
}

// Convenience for a saved-set snapshot ({ nodes, setPath }).
export function estimateSavedSetSeconds(set) {
    const tracks = (set?.setPath || []).map(id => set.nodes?.[id]?.track).filter(Boolean);
    return estimateSetSeconds(tracks);
}
